import React from 'react';
import {
  Utensils, Beef, Fish, Egg, Salad, Sandwich, Croissant, Milk, Apple,
  Pizza, Soup, CupSoda, Store, Drumstick, Wheat,
} from 'lucide-react';
import { useMealImageSrc, isPrivateMealPhotoMarker } from '../MealImage';

/**
 * FoodAvatar — the single source of truth for food/brand row imagery across
 * Log Food (Foods, Restaurants, restaurant menus, recents, Meals, Barcode/
 * manual, grouped custom meals, AI logs).
 *
 * Rules:
 *   - Render the real food/brand image when the API provides a usable http(s) URL
 *     (a private AI-photo marker is resolved to one first).
 *   - On image load error (or no valid URL), render a deliberate app-styled
 *     Lucide category icon — NEVER a text emoji (Inter renders unsupported
 *     emoji as a boxed "?").
 */
function pickIcon(item) {
  if (!item) return Utensils;
  const name = String(item.name || '').toLowerCase();
  const category = String(item.category || '').toUpperCase();
  if (item.restaurant) return Store;
  if (/chicken|turkey|poultry|nugget|wing/.test(name)) return Drumstick;
  if (/beef|steak|burger|pork|bacon|meat|lamb/.test(name)) return Beef;
  if (/fish|salmon|tuna|cod|shrimp|seafood/.test(name)) return Fish;
  if (/egg|omelet/.test(name)) return Egg;
  if (/salad|lettuce|greens|veg/.test(name)) return Salad;
  if (/pizza/.test(name)) return Pizza;
  if (/soup|broth|chili|stew/.test(name)) return Soup;
  if (/burrito|wrap|taco|sandwich|sub|roll|bagel|toast|bread/.test(name)) return Sandwich;
  if (/croissant|pastry|muffin|donut|cake|cookie/.test(name)) return Croissant;
  if (/yogurt|milk|cheese|latte/.test(name)) return Milk;
  if (/apple|fruit|berry|banana|orange|grape/.test(name)) return Apple;
  if (/water|drink|beverage|juice|coffee|tea|soda|cola|shake|smoothie/.test(name)) return CupSoda;
  if (/rice|grain|quinoa|oat|pasta|noodle|cereal/.test(name)) return Wheat;
  if (category === 'MEAL' || category === 'CUSTOM') return Utensils;
  return Utensils;
}

function isUsableUrl(url) {
  return typeof url === 'string' && /^https?:\/\//i.test(url.trim());
}

export default function FoodAvatar({ item, size = 40, className = '', iconSize }) {
  const [broken, setBroken] = React.useState(false);
  const raw = item && item.image_url;
  // A saved AI photo is the private marker "/api/ai-log/photo/{meal_id}": resolve it
  // through the authenticated endpoint (memory-only signed URL) instead of dropping it.
  const isMarker = isPrivateMealPhotoMarker(raw);
  const resolved = useMealImageSrc(isMarker ? raw : '');
  const url = isMarker ? resolved : raw;
  const showImage = isUsableUrl(url) && !broken;
  const px = size + 'px';
  const Icon = pickIcon(item);
  const isz = iconSize || Math.round(size * 0.5);

  if (showImage) {
    return (
      <span
        className={'shrink-0 rounded-lg overflow-hidden bg-secondary flex items-center justify-center ' + className}
        style={{ width: px, height: px }}
        data-testid="food-avatar-image"
      >
        <img
          src={url}
          alt={(item && item.name) || 'food'}
          className="w-full h-full object-cover"
          onError={() => setBroken(true)}
          loading="lazy"
        />
      </span>
    );
  }

  return (
    <span
      className={'shrink-0 rounded-lg bg-accent/10 text-accent flex items-center justify-center ' + className}
      style={{ width: px, height: px }}
      data-testid="food-avatar-fallback"
      aria-hidden="true"
    >
      <Icon size={isz} />
    </span>
  );
}
